import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWallet } from '@solana/wallet-adapter-react'
import { motion } from 'framer-motion'
import { User, TrendingUp, Brain, Award, DollarSign, Target, Calendar, CheckCircle, XCircle, Star, BarChart3, History, Trophy, Edit3, Lock, Clock, Coins } from 'lucide-react'
import axios from 'axios'
import toast from 'react-hot-toast'
import ProfileSetupModal from '../components/ProfileSetupModal'
import { useAgiAuthStore } from '../store/agiAuthStore'

interface UserProfile {
  id: number
  wallet_address: string
  username: string | null
  email: string | null
  created_at: string
  total_markets_created: number
  successful_resolutions: number
  reputation_score: number
  total_bets: number
  total_wagered: number
  total_winnings: number
}

interface Market {
  id: number
  question: string
  status: string
  category: string
  total_volume: number
  outcome: number | null
  created_at: string
}

interface Bet {
  id: number
  market_id: number
  market_question: string
  outcome: number
  amount: number
  claimed_amount: number | null
  claimed_at: string | null
  created_at: string
  market_status: string
  market_outcome: number | null
  resolved_at: string | null
}

interface FantasyEntry {
  id: number
  contest_id: number
  contest_title: string
  contest_description: string
  sport: string
  contest_type: string
  contest_category?: string  // 'dfs' or 'survivor'
  contest_status: string
  roster_lock_time: string
  resolution_time: string
  entry_fee: number
  prize_pool: number
  roster: Record<string, string>
  playerPool: any[]
  playerScores: any[]
  total_points: number | null
  rank: number | null
  isLocked: boolean
  canEdit: boolean
  rolling_lock?: number
  game_windows?: any[]
  entry_number?: number
  entry_name?: string
  captain_pick?: string  // gameId of captain pick for survivor contests
}

interface AgiBet {
  id: number
  market_id: number
  market_question: string
  outcome: number
  amount: number
  payout: number | null
  created_at: string
  market_status: string
  market_outcome: number | null
}

export default function ProfilePage() {
  const { connected, publicKey } = useWallet()
  const navigate = useNavigate()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [createdMarkets, setCreatedMarkets] = useState<Market[]>([])
  const [userBets, setUserBets] = useState<Bet[]>([])
  const [fantasyEntries, setFantasyEntries] = useState<FantasyEntry[]>([])
  const [agiBets, setAgiBets] = useState<AgiBet[]>([])
  const [loading, setLoading] = useState(true)
  const [showSetup, setShowSetup] = useState(false)
  const [activeTab, setActiveTab] = useState<string>('mymarkets')
  const [favorites, setFavorites] = useState<number[]>([]) // Array of market IDs
  
  // AGI Auth
  const { agiUser, isAgiAuthenticated, agiToken } = useAgiAuthStore()

  useEffect(() => {
    if (connected && publicKey) {
      loadProfile()
      loadFavorites()
    } else {
      setLoading(false)
    }
  }, [connected, publicKey])
  
  // Load AGI bets when AGI authenticated
  useEffect(() => {
    if (isAgiAuthenticated && agiUser) {
      loadAgiBets()
      // Auto-switch to AGI Credits tab if not wallet connected
      if (!connected) {
        setActiveTab('agicredits')
      }
    }
  }, [isAgiAuthenticated, agiUser, connected])
  
  // Load favorites from localStorage
  const loadFavorites = () => {
    const stored = localStorage.getItem(`favorites_${publicKey?.toString()}`)
    if (stored) {
      setFavorites(JSON.parse(stored))
    }
  }
  
  // Load AGI bets
  const loadAgiBets = async () => {
    if (!agiToken) return
    try {
      const response = await axios.get('/api/agi/my-bets', {
        headers: { Authorization: `Bearer ${agiToken}` }
      })
      setAgiBets(response.data.bets || [])
      console.log('✅ Loaded', response.data.bets?.length || 0, 'AGI bets')
    } catch (error) {
      console.error('Failed to load AGI bets:', error)
      setAgiBets([])
    }
  }
  
  // Toggle favorite
  const toggleFavorite = (marketId: number) => {
    const newFavorites = favorites.includes(marketId)
      ? favorites.filter(id => id !== marketId)
      : [...favorites, marketId]
    
    setFavorites(newFavorites)
    localStorage.setItem(`favorites_${publicKey?.toString()}`, JSON.stringify(newFavorites))
    toast.success(favorites.includes(marketId) ? 'Removed from favorites' : 'Added to favorites!')
  }

  const loadProfile = async () => {
    try {
      setLoading(true)
      const walletAddr = publicKey?.toString()
      
      if (!walletAddr) return
      
      // Get profile by wallet address (no auth required)
      const profileRes = await axios.get(`/api/users/wallet/${walletAddr}`)
      
      if (!profileRes.data.username) {
        // First time user - show setup
        setShowSetup(true)
        setProfile(profileRes.data) // Set basic profile
      } else {
        setProfile(profileRes.data)
      }
      
      // Load markets created by this wallet
      const marketsRes = await axios.get(`/api/markets?creator=${walletAddr}`)
      setCreatedMarkets(marketsRes.data.markets || [])
      
      // Load bets by wallet address
      try {
        const betsRes = await axios.get(`/api/users/wallet/${walletAddr}/bets`)
        setUserBets(betsRes.data.bets || [])
        console.log('✅ Loaded', betsRes.data.bets?.length || 0, 'bets for user')
      } catch (error) {
        console.error('Failed to load bets:', error)
        setUserBets([])
      }
      
      // Load fantasy entries by user ID (need profile ID)
      if (profileRes.data?.id) {
        try {
          const fantasyRes = await axios.get(`/api/fantasy/user/${profileRes.data.id}/entries`)
          setFantasyEntries(fantasyRes.data.entries || [])
          console.log('✅ Loaded', fantasyRes.data.entries?.length || 0, 'fantasy entries for user')
        } catch (error) {
          console.error('Failed to load fantasy entries:', error)
          setFantasyEntries([])
        }
      }
      
    } catch (error: any) {
      console.error('Failed to load profile:', error)
      if (error.response?.status === 404) {
        // User doesn't exist yet - show setup
        setShowSetup(true)
      } else {
        toast.error('Failed to load profile')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleProfileComplete = async (userData: { username: string; email: string }) => {
    setShowSetup(false)
    await loadProfile()
  }

  // Allow access if wallet connected OR AGI authenticated
  if (!connected && !isAgiAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <User className="w-16 h-16 text-cpx-light-blue mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Login Required</h2>
          <p className="text-gray-300 mb-6">Connect your wallet or login with AGI email to view your profile</p>
          <button 
            className="bg-gradient-to-r from-[#2AAAEA] to-[#052393] hover:shadow-lg hover:shadow-[#2AAAEA]/50 text-white px-6 py-3 rounded-lg font-bold transition-all" 
            onClick={() => navigate('/')}
          >
            Go Home
          </button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2AAAEA] mx-auto mb-4"></div>
          <p className="text-gray-300">Loading profile...</p>
        </div>
      </div>
    )
  }

  // Calculate stats from the actual bets data
  const totalSpent = userBets.reduce((sum, bet) => sum + bet.amount, 0)
  const totalBets = userBets.length
  
  // CLAIMABLE: Won but not claimed yet
  const claimableBets = userBets.filter(b => 
    b.market_status === 'resolved' && 
    b.market_outcome === b.outcome && 
    !b.claimed_at
  )
  const totalClaimable = claimableBets.reduce((sum, b) => sum + b.amount, 0)
  
  // CLAIMED: Already collected
  const alreadyClaimedBets = userBets.filter(b => b.claimed_at)
  const totalClaimed = alreadyClaimedBets.reduce((sum, b) => sum + (b.claimed_amount || b.amount), 0)
  
  // LOST: Bet on wrong side
  const lostBets = userBets.filter(b => 
    b.market_status === 'resolved' && 
    b.market_outcome !== b.outcome
  )
  const totalLost = lostBets.reduce((sum, b) => sum + b.amount, 0)
  
  // PENDING: Not resolved yet
  const pendingBets = userBets.filter(b => b.market_status !== 'resolved')
  const totalPending = pendingBets.reduce((sum, b) => sum + b.amount, 0)
  
  const activeBets = pendingBets.length
  const claimedBets = alreadyClaimedBets.length

  return (
    <>
      <ProfileSetupModal
        isOpen={showSetup}
        walletAddress={publicKey?.toString() || ''}
        onComplete={handleProfileComplete}
      />

      <div className="min-h-screen py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Profile Header - Gradient Design */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative overflow-hidden rounded-xl mb-6"
          >
            {/* Gradient Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#2AAAEA] via-[#052393] to-[#FBB03B] opacity-20"></div>
            <div className="absolute inset-0 bg-[#001233]"></div>
            
            {/* Content */}
            <div className="relative p-8 border border-[#2AAAEA]/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#2AAAEA] via-[#052393] to-[#FBB03B] rounded-full flex items-center justify-center">
                      <User className="w-10 h-10 text-white" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gradient-to-br from-[#009245] to-[#FBB03B] rounded-full flex items-center justify-center">
                      <Award className="w-3 h-3 text-white" />
                    </div>
                  </div>
                  <div>
                    <h1 className="text-3xl font-black text-white mb-1">
                      {profile?.username || agiUser?.email?.split('@')[0] || 'User'}
                    </h1>
                    {(profile?.email || agiUser?.email) && (
                      <p className="text-gray-300 text-sm mb-1">{profile?.email || agiUser?.email}</p>
                    )}
                    {publicKey ? (
                      <p className="text-xs text-gray-500 font-mono">
                        {publicKey?.toString().slice(0, 12)}...{publicKey?.toString().slice(-8)}
                      </p>
                    ) : isAgiAuthenticated && (
                      <p className="text-xs text-yellow-500 font-semibold">
                        ✨ AGI.live Member
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="text-right">
                  {!connected && isAgiAuthenticated ? (
                    <>
                      <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30">
                        <Coins className="w-5 h-5 text-yellow-400" />
                        <span className="text-2xl font-black text-yellow-400">{agiUser?.credits || 0}</span>
                      </div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mt-1">✨ Credits</p>
                    </>
                  ) : (
                    <>
                      <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#FBB03B]/20 to-[#009245]/20 border border-[#FBB03B]/30">
                        <Award className="w-5 h-5 text-cpx-yellow" />
                        <span className="text-2xl font-black text-cpx-yellow">{profile?.reputation_score || 0}</span>
                      </div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mt-1">Reputation</p>
                    </>
                  )}
                </div>
              </div>
              
              {/* Stats Bar with Gradient */}
              <div className="mt-8 pt-6 border-t border-[#2AAAEA]/20">
                {/* Show AGI stats when AGI-only user, wallet stats when wallet connected */}
                {!connected && isAgiAuthenticated ? (
                  <div className="grid grid-cols-4 gap-6">
                    <div className="text-center">
                      <div className="text-3xl font-black bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                        {agiUser?.credits || 0}
                      </div>
                      <div className="text-xs text-gray-400 uppercase tracking-wider mt-1">✨ Credits</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-black bg-gradient-to-r from-[#2AAAEA] to-[#FBB03B] bg-clip-text text-transparent">
                        {agiBets.length}
                      </div>
                      <div className="text-xs text-gray-400 uppercase tracking-wider mt-1">AGI Bets</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-black text-green-400">
                        {agiBets.filter(b => b.market_status === 'resolved' && b.market_outcome === b.outcome).length}
                      </div>
                      <div className="text-xs text-gray-400 uppercase tracking-wider mt-1">Won</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-black text-yellow-400">
                        {agiBets.filter(b => b.market_status !== 'resolved').length}
                      </div>
                      <div className="text-xs text-gray-400 uppercase tracking-wider mt-1">Pending</div>
                    </div>
                  </div>
                ) : (
                <div className="grid grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-black bg-gradient-to-r from-[#2AAAEA] to-[#052393] bg-clip-text text-transparent">
                      {createdMarkets.length}
                    </div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider mt-1">Markets</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-black bg-gradient-to-r from-[#2AAAEA] to-[#FBB03B] bg-clip-text text-transparent">
                      {totalBets}
                    </div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider mt-1">Bets Placed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-black text-white">
                      {(totalSpent / 1000000).toFixed(1)}M
                    </div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider mt-1">Total Wagered</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-black bg-gradient-to-r from-[#009245] to-[#FBB03B] bg-clip-text text-transparent">
                      {(totalClaimable / 1000000).toFixed(1)}M
                    </div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider mt-1">Claimable</div>
                  </div>
                </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* TAB NAVIGATION */}
          <div className="mb-6">
            <div className="flex items-center space-x-1 border-b border-gray-800/50">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-4 py-3 font-semibold transition-all duration-200 relative flex items-center space-x-2 ${
                  activeTab === 'overview'
                    ? 'text-white'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                <span>Overview</span>
                {activeTab === 'overview' && (
                  <motion.div
                    layoutId="profile-tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#2AAAEA] to-[#FBB03B]"
                    initial={false}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </button>
              <button
                onClick={() => setActiveTab('mymarkets')}
                className={`px-4 py-3 font-semibold transition-all duration-200 relative flex items-center space-x-2 ${
                  activeTab === 'mymarkets'
                    ? 'text-white'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                <span>My Markets</span>
                <span className="text-xs text-gray-500">({createdMarkets.length})</span>
                {activeTab === 'mymarkets' && (
                  <motion.div
                    layoutId="profile-tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#2AAAEA] to-[#FBB03B]"
                    initial={false}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </button>
              <button
                onClick={() => setActiveTab('claim')}
                className={`px-4 py-3 font-semibold transition-all duration-200 relative flex items-center space-x-2 ${
                  activeTab === 'claim'
                    ? 'text-white'
                    : 'text-cpx-green hover:text-white'
                }`}
              >
                <Award className="w-4 h-4" />
                <span>Ready to Claim</span>
                <span className="text-xs text-gray-500">({claimableBets.length})</span>
                {activeTab === 'claim' && (
                  <motion.div
                    layoutId="profile-tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#009245] to-[#FBB03B]"
                    initial={false}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-4 py-3 font-semibold transition-all duration-200 relative flex items-center space-x-2 ${
                  activeTab === 'history'
                    ? 'text-white'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                <History className="w-4 h-4" />
                <span>Bet History</span>
                <span className="text-xs text-gray-500">({userBets.length})</span>
                {activeTab === 'history' && (
                  <motion.div
                    layoutId="profile-tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#2AAAEA] to-[#FBB03B]"
                    initial={false}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </button>
              <button
                onClick={() => setActiveTab('favorites')}
                className={`px-4 py-3 font-semibold transition-all duration-200 relative flex items-center space-x-2 ${
                  activeTab === 'favorites'
                    ? 'text-white'
                    : 'text-cpx-yellow hover:text-white'
                }`}
              >
                <Star className="w-4 h-4" />
                <span>Favorites</span>
                <span className="text-xs text-gray-500">({favorites.length})</span>
                {activeTab === 'favorites' && (
                  <motion.div
                    layoutId="profile-tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#FBB03B] to-[#009245]"
                    initial={false}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </button>
              <button
                onClick={() => setActiveTab('fantasy')}
                className={`px-4 py-3 font-semibold transition-all duration-200 relative flex items-center space-x-2 ${
                  activeTab === 'fantasy'
                    ? 'text-white'
                    : 'text-emerald-400 hover:text-white'
                }`}
              >
                <Trophy className="w-4 h-4" />
                <span>🏈 Fantasy</span>
                <span className="text-xs text-gray-500">({fantasyEntries.length})</span>
                {activeTab === 'fantasy' && (
                  <motion.div
                    layoutId="profile-tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-500 to-green-500"
                    initial={false}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </button>
              {isAgiAuthenticated && (
                <button
                  onClick={() => setActiveTab('agicredits')}
                  className={`px-4 py-3 font-semibold transition-all duration-200 relative flex items-center space-x-2 ${
                    activeTab === 'agicredits'
                      ? 'text-white'
                      : 'text-yellow-400 hover:text-white'
                  }`}
                >
                  <Coins className="w-4 h-4" />
                  <span>✨ AGI Credits</span>
                  <span className="text-xs text-yellow-500 font-bold">({agiUser?.credits || 0})</span>
                  {activeTab === 'agicredits' && (
                    <motion.div
                      layoutId="profile-tab-indicator"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-yellow-500 to-orange-500"
                      initial={false}
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                </button>
              )}
            </div>
          </div>

          {/* TAB: Overview */}
          {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Bet Status Summary - Streamlined */}
            <div className="card p-6 border border-[#2AAAEA]/20">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Bet Status</h3>
              <div className="grid grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-black bg-gradient-to-r from-[#009245] to-emerald-500 bg-clip-text text-transparent mb-2">
                    {claimableBets.length}
                  </div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Ready to Claim</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black bg-gradient-to-r from-[#2AAAEA] to-[#052393] bg-clip-text text-transparent mb-2">
                    {alreadyClaimedBets.length}
                  </div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Claimed</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-red-400 mb-2">{lostBets.length}</div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Lost</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black bg-gradient-to-r from-[#FBB03B] to-[#009245] bg-clip-text text-transparent mb-2">
                    {pendingBets.length}
                  </div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Active</div>
                </div>
              </div>
            </div>
          </motion.div>
          )}

          {/* TAB: My Markets */}
          {activeTab === 'mymarkets' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card p-6"
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center">
              <img src="/cpx-logo.jpg" alt="CPX" className="w-6 h-6 rounded mr-2" />
              Your Markets ({createdMarkets.length})
            </h2>
            
            {createdMarkets.length > 0 ? (
              <div className="space-y-3">
                {createdMarkets.map((market) => (
                  <div
                    key={market.id}
                    className="border border-[#052393]/40 bg-[#001a3d] rounded-lg p-4 hover:border-[#2AAAEA]/60 hover:shadow-lg hover:shadow-[#2AAAEA]/20 cursor-pointer transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1" onClick={() => navigate(`/markets/${market.id}`)}>
                        <h3 className="font-semibold text-white mb-1">{market.question}</h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-400">
                          <span className="capitalize text-cpx-light-blue">{market.category}</span>
                          <span>•</span>
                          <span>{(market.total_volume / 1000000).toFixed(1)}M volume</span>
                          <span>•</span>
                          <span>{new Date(market.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {market.status === 'resolved' ? (
                          market.outcome === 1 ? (
                            <span className="flex items-center text-cpx-green text-sm font-semibold">
                              <CheckCircle className="w-4 h-4 mr-1" />
                              YES
                            </span>
                          ) : (
                            <span className="flex items-center text-red-400 text-sm font-semibold">
                              <XCircle className="w-4 h-4 mr-1" />
                              NO
                            </span>
                          )
                        ) : (
                          <span className="px-3 py-1 bg-[#009245]/20 text-cpx-green rounded-full text-xs font-semibold border border-[#009245]/30">
                            {market.status}
                          </span>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleFavorite(market.id)
                          }}
                          className={`p-2 rounded-lg transition-colors ${
                            favorites.includes(market.id)
                              ? 'text-cpx-yellow bg-[#FBB03B]/20 hover:bg-[#FBB03B]/30'
                              : 'text-gray-400 hover:text-cpx-yellow hover:bg-[#FBB03B]/10'
                          }`}
                        >
                          <Star className={`w-5 h-5 ${favorites.includes(market.id) ? 'fill-current' : ''}`} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-400 py-8">
                <img src="/cpx-logo.jpg" alt="CPX" className="w-12 h-12 rounded-lg mx-auto mb-3 opacity-50" />
                <p className="text-white mb-4">You haven't created any markets yet</p>
                <button
                  onClick={() => navigate('/create')}
                  className="bg-gradient-to-r from-[#2AAAEA] to-[#052393] hover:shadow-lg hover:shadow-[#2AAAEA]/50 text-white px-6 py-3 rounded-lg font-bold transition-all"
                >
                  Create Your First Market
                </button>
              </div>
            )}
          </motion.div>
          )}

          {/* TAB: Ready to Claim */}
          {activeTab === 'claim' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card p-6"
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center">
              <Award className="w-6 h-6 mr-2 text-cpx-green" />
              Ready to Claim ({claimableBets.length})
            </h2>
            
            {claimableBets.length > 0 ? (
              <div className="space-y-3">
                
                        {claimableBets.map((bet) => (
                          <div
                            key={bet.id}
                    className="border-2 border-[#009245] bg-[#009245]/10 rounded-lg p-4 hover:bg-[#009245]/20 hover:shadow-lg hover:shadow-[#009245]/30 cursor-pointer transition-all"
                          >
                            <div className="flex items-start justify-between">
                      <div className="flex-1" onClick={() => navigate(`/markets/${bet.market_id}`)}>
                                <h4 className="font-semibold text-white mb-1">{bet.market_question}</h4>
                                <div className="flex items-center space-x-4 text-sm text-gray-300">
                          <span className={`font-bold ${bet.outcome === 1 ? 'text-cpx-green' : 'text-red-400'}`}>
                                    Bet: {bet.outcome === 1 ? 'YES' : 'NO'}
                                  </span>
                                  <span>•</span>
                                  <span className="font-semibold">{(bet.amount / 1000000).toFixed(1)}M tokens</span>
                                  <span>•</span>
                                  <span>{new Date(bet.created_at).toLocaleDateString()}</span>
                                </div>
                              </div>
                      <div className="flex items-center space-x-2">
                              <div className="text-right">
                          <span className="flex items-center text-cpx-green text-lg font-bold mb-1">
                                  <CheckCircle className="w-5 h-5 mr-1" />
                                  WON!
                                </span>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/markets/${bet.market_id}`)
                            }}
                            className="px-3 py-1 bg-gradient-to-r from-[#009245] to-emerald-600 text-white rounded-full text-xs font-bold hover:shadow-lg transition-all"
                          >
                                  CLAIM NOW
                          </button>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleFavorite(bet.market_id)
                          }}
                          className={`p-2 rounded-lg transition-colors ${
                            favorites.includes(bet.market_id)
                              ? 'text-cpx-yellow bg-[#FBB03B]/20 hover:bg-[#FBB03B]/30'
                              : 'text-gray-400 hover:text-cpx-yellow hover:bg-[#FBB03B]/10'
                          }`}
                        >
                          <Star className={`w-5 h-5 ${favorites.includes(bet.market_id) ? 'fill-current' : ''}`} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
            ) : (
              <div className="text-center text-gray-400 py-8">
                <Award className="w-12 h-12 text-cpx-green mx-auto mb-3 opacity-50" />
                <p className="text-white mb-2">No winnings ready to claim</p>
                <p className="text-sm text-gray-400">Win a bet to see winnings here!</p>
                    </div>
                )}
          </motion.div>
          )}

          {/* TAB: Bet History */}
          {activeTab === 'history' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card p-6"
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center">
              <Target className="w-6 h-6 mr-2 text-cpx-light-blue" />
              All Bets ({userBets.length})
            </h2>
            
            {userBets.length > 0 ? (
              <div className="space-y-6">
                
                {/* ALREADY CLAIMED - Success Section */}
                {alreadyClaimedBets.length > 0 && (
                    <div>
                      <h3 className="text-lg font-bold text-cpx-light-blue mb-3 flex items-center justify-between">
                        <span className="flex items-center">
                          <CheckCircle className="w-5 h-5 mr-2" />
                          Claimed Winnings ({alreadyClaimedBets.length})
                        </span>
                        <span className="text-sm text-cpx-green">
                          {(totalClaimed / 1000000).toFixed(1)}M tokens collected
                        </span>
                      </h3>
                      <div className="space-y-2">
                        {alreadyClaimedBets.map((bet) => (
                          <div
                            key={bet.id}
                            className="border border-[#2AAAEA]/30 bg-[#001a3d] rounded-lg p-4 hover:border-[#2AAAEA]/60 hover:bg-[#052393]/20 cursor-pointer transition-all"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1" onClick={() => navigate(`/markets/${bet.market_id}`)}>
                                <h4 className="font-semibold text-white mb-1">{bet.market_question}</h4>
                                <div className="flex items-center space-x-4 text-sm text-gray-300">
                                  <span className={`font-semibold ${bet.outcome === 1 ? 'text-cpx-green' : 'text-red-400'}`}>
                                    Bet: {bet.outcome === 1 ? 'YES' : 'NO'}
                                  </span>
                                  <span>•</span>
                                  <span>{(bet.amount / 1000000).toFixed(1)}M staked</span>
                                  <span>•</span>
                                  <span className="text-cpx-green font-bold">
                                    Won: {((bet.claimed_amount || 0) / 1000000).toFixed(1)}M
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                              <div className="text-right">
                                  <span className="flex items-center text-cpx-light-blue text-sm font-semibold mb-1">
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Claimed
                                </span>
                                  <p className="text-xs text-gray-400">
                                  {new Date(bet.claimed_at).toLocaleDateString()}
                                </p>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    toggleFavorite(bet.market_id)
                                  }}
                                  className={`p-2 rounded-lg transition-colors ${
                                    favorites.includes(bet.market_id)
                                      ? 'text-cpx-yellow bg-[#FBB03B]/20 hover:bg-[#FBB03B]/30'
                                      : 'text-gray-400 hover:text-cpx-yellow hover:bg-[#FBB03B]/10'
                                  }`}
                                >
                                  <Star className={`w-5 h-5 ${favorites.includes(bet.market_id) ? 'fill-current' : ''}`} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                )}
                
                {/* LOST BETS - Info Section */}
                {lostBets.length > 0 && (
                    <div>
                      <h3 className="text-lg font-bold text-red-400 mb-3 flex items-center justify-between">
                        <span className="flex items-center">
                          <XCircle className="w-5 h-5 mr-2" />
                          Lost Bets ({lostBets.length})
                        </span>
                        <span className="text-sm text-gray-300">
                          {(totalLost / 1000000).toFixed(1)}M tokens lost
                        </span>
                      </h3>
                      <div className="space-y-2">
                        {lostBets.map((bet) => (
                  <div
                    key={bet.id}
                            className="border border-red-500/30 bg-red-900/10 rounded-lg p-4 hover:bg-red-900/20 hover:border-red-500/50 cursor-pointer transition-all opacity-75"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1" onClick={() => navigate(`/markets/${bet.market_id}`)}>
                                <h4 className="font-semibold text-white mb-1">{bet.market_question}</h4>
                                <div className="flex items-center space-x-4 text-sm text-gray-300">
                          <span className={`font-semibold ${bet.outcome === 1 ? 'text-cpx-green' : 'text-red-400'}`}>
                            Bet: {bet.outcome === 1 ? 'YES' : 'NO'}
                          </span>
                          <span>•</span>
                          <span>{(bet.amount / 1000000).toFixed(1)}M tokens</span>
                          <span>•</span>
                                  <span className="text-red-400 font-semibold">
                                    Won: {bet.market_outcome === 1 ? 'YES' : 'NO'}
                                  </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                                <span className="flex items-center text-red-400 text-sm font-semibold">
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Lost
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                toggleFavorite(bet.market_id)
                              }}
                              className={`p-2 rounded-lg transition-colors ${
                                favorites.includes(bet.market_id)
                                  ? 'text-cpx-yellow bg-[#FBB03B]/20 hover:bg-[#FBB03B]/30'
                                  : 'text-gray-400 hover:text-cpx-yellow hover:bg-[#FBB03B]/10'
                              }`}
                            >
                              <Star className={`w-5 h-5 ${favorites.includes(bet.market_id) ? 'fill-current' : ''}`} />
                            </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                )}
                
                {/* PENDING BETS - Active Markets */}
                {pendingBets.length > 0 && (
                    <div>
                      <h3 className="text-lg font-bold text-cpx-yellow mb-3 flex items-center justify-between">
                        <span className="flex items-center">
                          <Target className="w-5 h-5 mr-2" />
                          Pending Bets ({pendingBets.length})
                        </span>
                        <span className="text-sm text-gray-300">
                          {(totalPending / 1000000).toFixed(1)}M tokens at risk
                        </span>
                      </h3>
                      <div className="space-y-2">
                        {pendingBets.map((bet) => (
                          <div
                            key={bet.id}
                            className="border border-[#FBB03B]/30 bg-[#FBB03B]/10 rounded-lg p-4 hover:bg-[#FBB03B]/20 hover:border-[#FBB03B]/50 cursor-pointer transition-all"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1" onClick={() => navigate(`/markets/${bet.market_id}`)}>
                                <h4 className="font-semibold text-white mb-1">{bet.market_question}</h4>
                                <div className="flex items-center space-x-4 text-sm text-gray-300">
                                  <span className={`font-semibold ${bet.outcome === 1 ? 'text-cpx-green' : 'text-red-400'}`}>
                                    Bet: {bet.outcome === 1 ? 'YES' : 'NO'}
                                  </span>
                                  <span>•</span>
                                  <span>{(bet.amount / 1000000).toFixed(1)}M tokens</span>
                                  <span>•</span>
                                  <span className="capitalize">{bet.market_status}</span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="px-3 py-1 bg-[#FBB03B]/20 text-cpx-yellow rounded-full text-xs font-semibold border border-[#FBB03B]/30">
                                  {bet.market_status === 'active' ? 'Active' : 'Pending'}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleFavorite(bet.market_id)
                            }}
                            className={`p-2 rounded-lg transition-colors ${
                              favorites.includes(bet.market_id)
                                ? 'text-cpx-yellow bg-[#FBB03B]/20 hover:bg-[#FBB03B]/30'
                                : 'text-gray-400 hover:text-cpx-yellow hover:bg-[#FBB03B]/10'
                            }`}
                          >
                            <Star className={`w-5 h-5 ${favorites.includes(bet.market_id) ? 'fill-current' : ''}`} />
                          </button>
                      </div>
                    </div>
                  </div>
                ))}
                      </div>
                    </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-400 py-8">
                <Target className="w-12 h-12 text-cpx-light-blue mx-auto mb-3 opacity-50" />
                <p className="text-white mb-2">You haven't placed any bets yet</p>
                <button
                  onClick={() => navigate('/')}
                  className="bg-gradient-to-r from-[#2AAAEA] to-[#052393] hover:shadow-lg hover:shadow-[#2AAAEA]/50 text-white px-6 py-3 rounded-lg font-bold transition-all mt-4"
                >
                  Browse Markets
                </button>
              </div>
            )}
          </motion.div>
          )}

          {/* TAB: Favorites */}
          {activeTab === 'favorites' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card p-6"
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center">
              <Star className="w-6 h-6 mr-2 text-cpx-yellow fill-current" />
              Your Favorites ({favorites.length})
            </h2>
            
            {favorites.length > 0 ? (
              <div className="space-y-3">
                {favorites.map((marketId) => {
                  // Find market in created markets or bets
                  const market = createdMarkets.find(m => m.id === marketId) || 
                    userBets.find(b => b.market_id === marketId)
                  
                  if (!market) return null
                  
                  const isCreatedMarket = createdMarkets.some(m => m.id === marketId)
                  const question = isCreatedMarket ? (market as Market).question : (market as Bet).market_question
                  const status = isCreatedMarket ? (market as Market).status : (market as Bet).market_status
                  
                  return (
                    <div
                      key={marketId}
                      className="border-2 border-[#FBB03B]/40 bg-[#FBB03B]/5 rounded-lg p-4 hover:border-[#FBB03B]/60 hover:bg-[#FBB03B]/10 hover:shadow-lg hover:shadow-[#FBB03B]/20 cursor-pointer transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1" onClick={() => navigate(`/markets/${marketId}`)}>
                          <h3 className="font-semibold text-white mb-1">{question}</h3>
                          <div className="flex items-center space-x-4 text-sm text-gray-400">
                            <span className="capitalize text-cpx-yellow">{isCreatedMarket ? 'Your Market' : 'Your Bet'}</span>
                            <span>•</span>
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                              status === 'resolved' ? 'bg-[#009245]/20 text-cpx-green' :
                              status === 'active' ? 'bg-[#2AAAEA]/20 text-cpx-light-blue' :
                              'bg-[#052393]/30 text-gray-300'
                            }`}>
                              {status}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleFavorite(marketId)
                          }}
                          className="p-2 rounded-lg transition-colors text-cpx-yellow bg-[#FBB03B]/20 hover:bg-[#FBB03B]/30"
                        >
                          <Star className="w-5 h-5 fill-current" />
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center text-gray-400 py-8">
                <Star className="w-12 h-12 text-cpx-yellow mx-auto mb-3 opacity-50" />
                <p className="text-white mb-2">No favorites yet</p>
                <p className="text-sm text-gray-400">Click the star on any market to add it to your favorites!</p>
              </div>
            )}
          </motion.div>
          )}

          {/* TAB: Fantasy Entries */}
          {activeTab === 'fantasy' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card p-6"
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center">
              <Trophy className="w-6 h-6 mr-2 text-emerald-400" />
              Your Fantasy Entries ({fantasyEntries.length})
            </h2>
            
            {fantasyEntries.length > 0 ? (
              <>
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-800/50 rounded-lg p-4 text-center border border-slate-700/50">
                  <div className="text-3xl font-black text-emerald-400">{fantasyEntries.length}</div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider mt-1">Total Entries</div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4 text-center border border-slate-700/50">
                  <div className="text-3xl font-black text-blue-400">
                    {new Set(fantasyEntries.map(e => e.contest_id)).size}
                  </div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider mt-1">Contests</div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4 text-center border border-slate-700/50">
                  <div className="text-3xl font-black text-amber-400">
                    {fantasyEntries.reduce((sum, e) => sum + (e.total_points || 0), 0).toFixed(1)}
                  </div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider mt-1">Total Points</div>
                </div>
              </div>
              <div className="space-y-4">
                {fantasyEntries.map((entry) => {
                  // Check if this is a survivor/team-based contest
                  const isSurvivor = entry.contest_category === 'survivor' || 
                    Object.keys(entry.roster || {}).some(k => k.startsWith('game_'))
                  
                  // For survivor: roster contains { game_0: "Team Name", game_1: "Team Name", ... }
                  // For DFS: roster contains { QB: "playerId", RB1: "playerId", ... }
                  const rosterPlayers = isSurvivor 
                    ? [] // Don't use player lookup for survivor
                    : Object.entries(entry.roster || {}).map(([slot, playerId]) => {
                        const player = entry.playerPool?.find((p: any) => p.id === playerId)
                        return { slot, player }
                      })
                  
                  // Get team picks for survivor contests
                  const survivorPicks = isSurvivor 
                    ? Object.entries(entry.roster || {}).map(([gameId, teamName]) => ({
                        gameId,
                        team: teamName as string,
                        isCaptain: entry.captain_pick === gameId
                      }))
                    : []
                  
                  return (
                    <div
                      key={entry.id}
                      className={`border-2 rounded-xl p-5 transition-all cursor-pointer ${
                        entry.canEdit
                          ? 'border-emerald-500/40 bg-emerald-500/5 hover:border-emerald-400/60 hover:bg-emerald-500/10 hover:shadow-lg hover:shadow-emerald-500/20'
                          : 'border-slate-600/40 bg-slate-800/30 hover:border-slate-500/60'
                      }`}
                      onClick={() => navigate(`/fantasy/${entry.contest_id}`)}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg">🏈</span>
                            <h3 className="font-bold text-white text-lg">{entry.contest_title}</h3>
                            {/* Show entry number/name if it's a multi-entry or named entry */}
                            {(entry.entry_number && entry.entry_number > 1) || entry.entry_name ? (
                              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded text-xs font-medium border border-blue-500/30">
                                {entry.entry_name || `Entry #${entry.entry_number}`}
                              </span>
                            ) : entry.entry_number === 1 ? (
                              <span className="px-2 py-0.5 bg-slate-600/40 text-gray-400 rounded text-xs font-medium">
                                Entry #1
                              </span>
                            ) : null}
                          </div>
                          <p className="text-sm text-gray-400">{entry.contest_description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {entry.rolling_lock === 1 && (
                            <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-[10px] font-medium border border-yellow-500/30">
                              🔄 Rolling Lock
                            </span>
                          )}
                          {entry.canEdit ? (
                            <span className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/20 text-emerald-400 rounded-lg text-xs font-bold border border-emerald-500/30">
                              <Edit3 className="w-3 h-3" />
                              Editable
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 px-3 py-1.5 bg-slate-700/50 text-gray-400 rounded-lg text-xs font-bold">
                              <Lock className="w-3 h-3" />
                              Locked
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Roster/Picks Preview with Scores */}
                      <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                            {isSurvivor ? 'Your Team Picks' : 'Your Roster'}
                          </h4>
                          {entry.total_points > 0 && (
                            <span className="text-xs text-gray-500">Click to view detailed breakdown</span>
                          )}
                        </div>
                        <div className="space-y-2">
                          {/* SURVIVOR CONTEST: Show team picks */}
                          {isSurvivor && survivorPicks.map(({ gameId, team, isCaptain }) => (
                            <div key={gameId} className={`flex items-center justify-between p-2 rounded-lg ${
                              isCaptain ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-slate-800/50'
                            }`}>
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                                  isCaptain ? 'text-amber-400 bg-amber-500/20' : 'text-emerald-400 bg-emerald-500/20'
                                }`}>
                                  {isCaptain ? '👑 CPT' : gameId.replace('game_', 'G')}
                                </span>
                                <span className="text-white text-sm font-medium">
                                  {team || 'Not picked'}
                                </span>
                              </div>
                              {isCaptain && (
                                <span className="text-xs text-amber-400 font-medium">2x pts</span>
                              )}
                            </div>
                          ))}
                          
                          {/* DFS CONTEST: Show player roster */}
                          {!isSurvivor && rosterPlayers.map(({ slot, player }) => {
                            // Get this player's score from playerScores
                            const playerScore = entry.playerScores?.find((p: any) => p.playerId === player?.id)
                            
                            return (
                              <div key={slot} className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-bold text-emerald-400 bg-emerald-500/20 px-2 py-0.5 rounded">
                                    {slot}
                                  </span>
                                  <div>
                                    <span className="text-white text-sm">
                                      {player?.name || 'Empty'}
                                    </span>
                                    {player && (
                                      <span className="text-xs text-gray-500 ml-2">
                                        {player.team} • ${player.salary?.toLocaleString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Player Points */}
                                {playerScore && (
                                  <div className="text-right">
                                    <span className="text-emerald-400 font-bold">
                                      {playerScore.fantasyPoints?.toFixed(1) || '0.0'}
                                    </span>
                                    <span className="text-xs text-gray-500 ml-1">pts</span>
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                        
                        {/* Detailed Score Breakdown (expandable) */}
                        {entry.playerScores && entry.playerScores.length > 0 && (
                          <details className="mt-3">
                            <summary className="cursor-pointer text-xs text-amber-400 hover:text-amber-300 font-medium">
                              📊 View Detailed Score Breakdown
                            </summary>
                            <div className="mt-3 space-y-3">
                              {entry.playerScores.map((ps: any) => (
                                <div key={ps.playerId} className="border border-slate-700/50 rounded-lg p-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="font-medium text-white">{ps.playerName}</span>
                                    <span className="text-emerald-400 font-bold">{ps.fantasyPoints?.toFixed(1)} pts</span>
                                  </div>
                                  {ps.pointBreakdown && Object.keys(ps.pointBreakdown).length > 0 && (
                                    <div className="grid grid-cols-2 gap-1 text-xs">
                                      {Object.entries(ps.pointBreakdown)
                                        .filter(([_, pts]) => pts !== 0)
                                        .map(([stat, pts]) => (
                                          <div key={stat} className="flex justify-between">
                                            <span className="text-gray-500">{stat}</span>
                                            <span className={(pts as number) > 0 ? 'text-emerald-400' : 'text-red-400'}>
                                              {(pts as number) > 0 ? '+' : ''}{(pts as number).toFixed(1)}
                                            </span>
                                          </div>
                                        ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                      
                      {/* Stats Row */}
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-1 text-gray-400">
                            <Clock className="w-4 h-4" />
                            <span>
                              {entry.isLocked 
                                ? `Locked ${new Date(entry.roster_lock_time).toLocaleDateString()}` 
                                : `Locks ${new Date(entry.roster_lock_time).toLocaleDateString()}`}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-gray-400">
                            <DollarSign className="w-4 h-4" />
                            <span>{entry.entry_fee > 0 ? `$${entry.entry_fee}` : 'Free'}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {entry.total_points !== null && (
                            <div className="flex items-center gap-1">
                              <span className="text-emerald-400 font-bold">{entry.total_points} pts</span>
                              {entry.rank && (
                                <span className="text-gray-500">• Rank #{entry.rank}</span>
                              )}
                            </div>
                          )}
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            entry.contest_status === 'open' ? 'bg-emerald-500/20 text-emerald-400' :
                            entry.contest_status === 'locked' ? 'bg-yellow-500/20 text-yellow-400' :
                            entry.contest_status === 'live' ? 'bg-red-500/20 text-red-400' :
                            entry.contest_status === 'resolved' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {entry.contest_status}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
              </>
            ) : (
              <div className="text-center text-gray-400 py-8">
                <Trophy className="w-12 h-12 text-emerald-500 mx-auto mb-3 opacity-50" />
                <p className="text-white mb-2">No fantasy entries yet</p>
                <p className="text-sm text-gray-400 mb-4">Enter a contest to see your picks here!</p>
                <button
                  onClick={() => navigate('/fantasy')}
                  className="bg-gradient-to-r from-emerald-600 to-green-600 hover:shadow-lg hover:shadow-emerald-500/50 text-white px-6 py-3 rounded-lg font-bold transition-all"
                >
                  Browse Fantasy Contests
                </button>
              </div>
            )}
          </motion.div>
          )}

          {/* TAB: AGI Credits */}
          {activeTab === 'agicredits' && isAgiAuthenticated && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* AGI Credits Header */}
            <div className="card p-6 border-2 border-yellow-500/30 bg-gradient-to-r from-yellow-500/10 to-orange-500/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center">
                    <Coins className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-white">AGI Credits</h2>
                    <p className="text-gray-400">{agiUser?.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-5xl font-black bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                    {agiUser?.credits || 0}
                  </div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mt-1">Available Credits</p>
                </div>
              </div>
            </div>

            {/* AGI Bet Stats */}
            <div className="card p-6 border border-yellow-500/20">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Credit Betting Stats</h3>
              <div className="grid grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-black text-yellow-400 mb-2">
                    {agiBets.length}
                  </div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Total Bets</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-green-400 mb-2">
                    {agiBets.filter(b => b.market_status === 'resolved' && b.market_outcome === b.outcome).length}
                  </div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Won</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-red-400 mb-2">
                    {agiBets.filter(b => b.market_status === 'resolved' && b.market_outcome !== b.outcome).length}
                  </div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Lost</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-blue-400 mb-2">
                    {agiBets.filter(b => b.market_status !== 'resolved').length}
                  </div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Pending</div>
                </div>
              </div>
            </div>

            {/* AGI Bet History */}
            <div className="card p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                <History className="w-6 h-6 mr-2 text-yellow-400" />
                AGI Credit Bet History ({agiBets.length})
              </h2>
              
              {agiBets.length > 0 ? (
                <div className="space-y-3">
                  {/* Won Bets */}
                  {agiBets.filter(b => b.market_status === 'resolved' && b.market_outcome === b.outcome).length > 0 && (
                    <div className="mb-4">
                      <h3 className="text-lg font-bold text-green-400 mb-3 flex items-center">
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Won ({agiBets.filter(b => b.market_status === 'resolved' && b.market_outcome === b.outcome).length})
                      </h3>
                      <div className="space-y-2">
                        {agiBets.filter(b => b.market_status === 'resolved' && b.market_outcome === b.outcome).map(bet => (
                          <div
                            key={bet.id}
                            className="border border-green-500/30 bg-green-500/10 rounded-lg p-4 hover:bg-green-500/20 cursor-pointer transition-all"
                            onClick={() => navigate(`/markets/${bet.market_id}`)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-white mb-1">{bet.market_question}</h4>
                                <div className="flex items-center space-x-4 text-sm text-gray-300">
                                  <span className={`font-bold ${bet.outcome === 1 ? 'text-green-400' : 'text-red-400'}`}>
                                    Bet: {bet.outcome === 1 ? 'YES' : 'NO'}
                                  </span>
                                  <span>•</span>
                                  <span>{bet.amount} credits staked</span>
                                  <span>•</span>
                                  <span className="text-green-400 font-bold">Won: +{bet.payout || bet.amount} credits</span>
                                </div>
                              </div>
                              <span className="flex items-center text-green-400 text-sm font-semibold">
                                <CheckCircle className="w-4 h-4 mr-1" />
                                WON
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Lost Bets */}
                  {agiBets.filter(b => b.market_status === 'resolved' && b.market_outcome !== b.outcome).length > 0 && (
                    <div className="mb-4">
                      <h3 className="text-lg font-bold text-red-400 mb-3 flex items-center">
                        <XCircle className="w-5 h-5 mr-2" />
                        Lost ({agiBets.filter(b => b.market_status === 'resolved' && b.market_outcome !== b.outcome).length})
                      </h3>
                      <div className="space-y-2">
                        {agiBets.filter(b => b.market_status === 'resolved' && b.market_outcome !== b.outcome).map(bet => (
                          <div
                            key={bet.id}
                            className="border border-red-500/30 bg-red-500/10 rounded-lg p-4 opacity-75 hover:opacity-100 cursor-pointer transition-all"
                            onClick={() => navigate(`/markets/${bet.market_id}`)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-white mb-1">{bet.market_question}</h4>
                                <div className="flex items-center space-x-4 text-sm text-gray-300">
                                  <span className={`font-bold ${bet.outcome === 1 ? 'text-green-400' : 'text-red-400'}`}>
                                    Bet: {bet.outcome === 1 ? 'YES' : 'NO'}
                                  </span>
                                  <span>•</span>
                                  <span>{bet.amount} credits lost</span>
                                </div>
                              </div>
                              <span className="flex items-center text-red-400 text-sm font-semibold">
                                <XCircle className="w-4 h-4 mr-1" />
                                LOST
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Pending Bets */}
                  {agiBets.filter(b => b.market_status !== 'resolved').length > 0 && (
                    <div>
                      <h3 className="text-lg font-bold text-yellow-400 mb-3 flex items-center">
                        <Target className="w-5 h-5 mr-2" />
                        Pending ({agiBets.filter(b => b.market_status !== 'resolved').length})
                      </h3>
                      <div className="space-y-2">
                        {agiBets.filter(b => b.market_status !== 'resolved').map(bet => (
                          <div
                            key={bet.id}
                            className="border border-yellow-500/30 bg-yellow-500/10 rounded-lg p-4 hover:bg-yellow-500/20 cursor-pointer transition-all"
                            onClick={() => navigate(`/markets/${bet.market_id}`)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-white mb-1">{bet.market_question}</h4>
                                <div className="flex items-center space-x-4 text-sm text-gray-300">
                                  <span className={`font-bold ${bet.outcome === 1 ? 'text-green-400' : 'text-red-400'}`}>
                                    Bet: {bet.outcome === 1 ? 'YES' : 'NO'}
                                  </span>
                                  <span>•</span>
                                  <span>{bet.amount} credits at risk</span>
                                </div>
                              </div>
                              <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs font-semibold border border-yellow-500/30">
                                Pending
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-gray-400 py-8">
                  <Coins className="w-12 h-12 text-yellow-500 mx-auto mb-3 opacity-50" />
                  <p className="text-white mb-2">No AGI credit bets yet</p>
                  <p className="text-sm text-gray-400 mb-4">Bet on AGI Events to see your history here!</p>
                  <button
                    onClick={() => navigate('/markets?category=agi_manual')}
                    className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:shadow-lg hover:shadow-yellow-500/50 text-white px-6 py-3 rounded-lg font-bold transition-all"
                  >
                    Browse AGI Events
                  </button>
                </div>
              )}
            </div>
          </motion.div>
          )}
        </div>
      </div>
    </>
  )
}
